import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { CONTACT, PRODUCTS, CTA_IMAGE } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", phone: "", product: PRODUCTS[0].name, message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. Our team will call you back shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send enquiry. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 font-sans text-plasma placeholder:text-plasma/30 focus:border-forge focus:outline-none transition-colors";

  return (
    <section id="contact" className="relative py-24 md:py-40 noise-overlay overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 opacity-20">
        <img src={CTA_IMAGE} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-void/70" />
      </div>

      <div className="relative mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="flex items-center gap-4 font-mono text-xs uppercase tracking-[0.25em] text-forge mb-8">
              <span className="w-10 h-px bg-forge" />
              Enquire / Get a Quote
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-head font-black uppercase tracking-tighter text-5xl md:text-7xl leading-[0.85] mb-10">
              Let's power <br /> <span className="text-forge">your project.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-plasma/65 leading-relaxed max-w-md mb-12">
              Tell us about your load, site and goals. An engineer — not a call centre —
              will get back to you with a tailored recommendation.
            </p>
          </Reveal>

          <div className="space-y-5">
            {[
              { icon: Phone, label: `${CONTACT.phone}  ·  ${CONTACT.phone2}`, href: `tel:${CONTACT.phone.replace(/\s/g, "")}`, id: "phone" },
              { icon: Mail, label: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "email" },
              { icon: MapPin, label: CONTACT.address, href: null, id: "address" },
            ].map(({ icon: Icon, label, href, id }) => (
              <Reveal key={id} delay={0.25}>
                <div className="flex items-start gap-4">
                  <span className="grid place-items-center w-10 h-10 border border-white/15 text-forge shrink-0">
                    <Icon size={16} />
                  </span>
                  {href ? (
                    <a href={href} data-testid={`contact-${id}`} className="text-plasma/80 hover:text-forge transition-colors pt-2.5 font-mono text-sm">
                      {label}
                    </a>
                  ) : (
                    <span className="text-plasma/80 pt-2.5 font-mono text-sm max-w-xs">{label}</span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              className="bg-void/70 backdrop-blur-xl border border-white/10 p-6 md:p-12 chamfer"
              data-testid="enquiry-form"
            >
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-plasma/50">Full Name</label>
                  <input required value={form.name} onChange={update("name")} data-testid="input-name" className={inputCls} placeholder="Rajesh Kumar" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-plasma/50">Phone</label>
                  <input required value={form.phone} onChange={update("phone")} data-testid="input-phone" className={inputCls} placeholder="+91 ..." />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-plasma/50">Email</label>
                  <input required type="email" value={form.email} onChange={update("email")} data-testid="input-email" className={inputCls} placeholder="you@company.com" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-plasma/50">Interested In</label>
                  <select value={form.product} onChange={update("product")} data-testid="input-product" className={inputCls + " appearance-none"}>
                    {PRODUCTS.map((p) => (
                      <option key={p.id} value={p.name} className="bg-void text-plasma">{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mb-10">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-plasma/50">Your Requirement</label>
                <textarea required rows={4} value={form.message} onChange={update("message")} data-testid="input-message" className={inputCls + " resize-none"} placeholder="Load details, site location, timeline..." />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="submit-enquiry"
                className="group inline-flex items-center gap-3 bg-plasma text-void font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-forge transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
                {loading ? "Sending" : "Send Enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
